import { AfterViewInit, Component, DoCheck, ElementRef, HostListener, Input, ViewChild, inject } from '@angular/core';
import { AuthService } from '../../auth.service';
import { personenangabenVerbergen } from '../personenangaben';
import { AufnahmePunktDocument, stfgString } from '../../data-model';
import {
	FeinbodenformZeile, feinbodenformLangform, feinbodenformText, hauptbodenformText, langformNachschlag,
} from '../detail/bodenform-anzeige';
import { DataService } from '../../data.service';
import { imWechsel, wechselLage, WechselLage } from '../detail/wechsel-gruppen';
import { HUMUS_STUFEN } from '../help/humus-stufen';
import { KARBONAT_STUFEN } from '../help/karbonat-stufen';
import { LAGERUNGSDICHTE_STUFEN } from '../help/lagerungsdichte-stufen';
import { SKELETT_STUFEN } from '../help/skelett-stufen';
import { HorizontZeile, SubstratZeile } from '../../data-model';
import { Abschnitt, abschnitte, AuflageStreifen, gesamtTiefe } from './grubenbild-abschnitte';
import { angezeigteTiefe } from '../filz-tiefen';
import { lupenOrt, zeichneAuflageLupe } from './auflage-lupe';
import { bodenartText } from './grubenbild-bodenarten';
import { grubenbildStand } from './grubenbild-daten';
import { zeichneGrubenbild } from './grubenbild-zeichnen';
import {
	HORIZONT_FARBEN, horizontFarbe, helligkeit, istWechsel, leseSymbol, symbolfolgeText, symbolText,
} from './horizont-farben';

/** Eine Zeile der Erklaerungstafel neben dem Bild. */
interface Angabe {
	name: string;
	wert: string;
}

/** Ein Eintrag der Legende unter dem Bild. */
interface LegendenEintrag {
	symbol: string;
	farbe: string;
	text: string;
}

/**
 * Schematisches Grubenbild aus Horizont- und Substratfolge.
 *
 * Die Wand ist 1,50 m breit und zeigt die ganze Aufnahmetiefe, maszstabsgerecht
 * in beiden Richtungen. Zeigen erklaert eine Schicht, ein Klick haelt sie in der
 * Tafel daneben fest - auf dem Tablet gibt es kein Zeigen, dort ist der Klick
 * der Weg.
 *
 * Gezeichnet wird ausserhalb von Angular (`grubenbild-zeichnen.ts`): Das Bild
 * besteht je nach Profil aus einigen tausend Koernern und Flecken, und die
 * gehoeren nicht in die Aenderungspruefung.
 */
@Component({
	standalone: false,
	selector: 'bp-grubenbild',
	templateUrl: './grubenbild.component.html',
	styleUrls: ['./grubenbild.component.css'],
})
export class GrubenbildComponent implements AfterViewInit, DoCheck {
	@Input() bp!: AufnahmePunktDocument;

	@ViewChild('bild') bildFeld?: ElementRef<HTMLDivElement>;
	@ViewChild('tafel') tafelFeld?: ElementRef<HTMLElement>;
	@ViewChild('inhalt') inhaltFeld?: ElementRef<HTMLElement>;

	/**
	 * Das Bild in der Lupe entsteht, sobald ihr Feld im Dokument steht - es
	 * liegt in einem `@if` und ist vorher nicht da. Gezeichnet wird wie das
	 * Grubenbild ausserhalb von Angular.
	 */
	@ViewChild('lupeBild') set lupeBildFeld(ref: ElementRef<HTMLDivElement> | undefined) {
		ref?.nativeElement.replaceChildren(zeichneAuflageLupe(this.auflage.zeilen));
	}

	/**
	 * Die Humusauflage vergroessert (Nutzer-Wunsch 21.09.2026): offen, wenn
	 * hier ein Ort steht. Ein Klick auf die Beschriftung von Ol, Of und Oh
	 * oeffnet sie, ein zweiter schliesst sie wieder.
	 */
	lupe: { x: number; y: number; breite: number } | null = null;

	private readonly aus = inject(AuthService);
	private readonly ds = inject(DataService);

	/**
	 * Der festgehaltene Horizont: Index in `Horizontfolge`, „auflage:Ol" oder
	 * nichts. Ein Klick waehlt den Horizont, das Zeigen erklaert die
	 * Substratschicht (Nutzer-Entscheidung 19.09.2026).
	 */
	gewaehlt: number | string | null = null;
	/** Beides fest an: Die Schalter dafuer sind auf Nutzerwunsch wieder raus. */
	readonly substratgrenzen = true;
	readonly textur = true;

	/**
	 * Die vier Anzeige-Schalter ueber dem Bild (Nutzer-Wunsch 24.09.2026):
	 * Grenzlinien (die dicken an den Horizont- UND die feinen Fugen an den
	 * Substratgrenzen, mit einem Haken), Wurzeln, Horizonte und Substrate
	 * lassen sich einzeln ausblenden - Horizonte und Substrate auch jeweils
	 * ohne das andere. Alle an; sie gelten fuer die Sitzung und werden nicht
	 * gemerkt.
	 *
	 * Ein Schalter aendert nur die Anzeige und steht deshalb NICHT im Stand
	 * (`standSammeln`): Der setzt beim Wechsel die gewaehlte Schicht zurueck,
	 * und die soll ein Haken nicht verlieren. Die Vorlage zeichnet direkt neu.
	 */
	grenzlinien = true;
	wurzeln = true;
	horizonte = true;
	substrate = true;

	/** Text und Ort des Tipps beim Zeigen. */
	tipp: string | null = null;
	tippX = 0;
	tippY = 0;

	private letzterStand = '';
	private abs: Abschnitt[] = [];
	private auflage: { zeilen: AuflageStreifen[]; gesamt: number } = { zeilen: [], gesamt: 0 };

	/**
	 * Filz: um so viel cm ruecken alle Tiefen in der Tafel nach unten - die
	 * Auflage gehoert dann zum Profil, 0 cm liegt an ihrer Oberkante
	 * (filz-tiefen.ts). 0 ohne Filz.
	 */
	versatz = 0;

	/** Eine Tiefe, wie sie in der Tafel steht: ab Mineralboden erfasst, bei Filz um die Auflage tiefer. */
	tiefe(cm: number): number {
		return angezeigteTiefe(cm, this.versatz) ?? cm;
	}

	ngAfterViewInit(): void {
		this.neuZeichnen();
	}

	/**
	 * Das Bild haengt an den erfassten Zeilen. Es wird nur neu gezeichnet, wenn
	 * sich an ihnen wirklich etwas geaendert hat - sonst baute jede Mausbewegung
	 * einige tausend SVG-Elemente neu auf.
	 */
	ngDoCheck(): void {
		const stand = this.standSammeln();
		if (stand !== this.letzterStand) {
			this.letzterStand = stand;
			this.gewaehlt = null;
			// Die Lupe zeigte die alte Auflage - sie geht mit zu.
			this.lupe = null;
			this.neuZeichnen();
		}
	}

	/** Oeffnet die Lupe unter dem Klick, oder schliesst sie, wenn sie offen ist. */
	private lupeUmschalten(klickX: number, klickY: number): void {
		const feld = this.inhaltFeld?.nativeElement;
		if (this.lupe || !feld) {
			this.lupe = null;
			return;
		}
		this.lupe = lupenOrt(klickX, klickY, feld.getBoundingClientRect());
	}

	lupeSchliessen(): void {
		this.lupe = null;
	}

	@HostListener('document:keydown.escape')
	beiEscape(): void {
		this.lupe = null;
	}

	private standSammeln(): string {
		const h = (this.bp?.Horizontfolge ?? [])
			.map(z => `${z.tiefe}:${(z.horizonte ?? []).join('/')}`).join('|');
		const s = (this.bp?.Substratfolge ?? [])
			.map(z => [z.tiefe, z.substrat, z.humusgehalt, z.skelettgehalt, z.karbonatgehalt,
				z.kiesgehalt, z.steingehalt, z.wechselschichtung].join(':')).join('|');
		const a = this.bp?.extendedHeader?.humusAuflage;
		return `${h}#${s}#${a?.ol}/${a?.of}/${a?.oh}#${this.substratgrenzen}${this.textur}`;
	}

	get hatInhalt(): boolean {
		return gesamtTiefe(abschnitte(this.bp?.Horizontfolge, this.bp?.Substratfolge)) > 0;
	}

	/**
	 * Der Tag der Aufnahme fuer die Zeile am Wasserstand, oder nichts.
	 *
	 * Im Gastmodus bleibt er weg - das Aufnahmedatum ist dort eine
	 * Personenangabe (dieselbe Regel wie ueberall, `personenangabenVerbergen`).
	 */
	private aufnahmeTag(): string | null {
		if (personenangabenVerbergen(this.aus)) return null;
		const roh = this.bp?.header?.aufnahme?.datum;
		if (!roh) return null;
		const d = new Date(roh);
		if (isNaN(d.getTime())) return String(roh);
		const zwei = (v: number) => String(v).padStart(2, '0');
		return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
	}

	/**
	 * Nach einer Wahl die Tafel in den Blick holen.
	 *
	 * Auf einem schmalen Schirm steht sie UNTER dem Bild. Wer dort auf eine
	 * Schicht tippt, saehe sonst nichts geschehen und wuesste nicht, dass die
	 * Auskunft weiter unten steht. `block: 'nearest'` tut nichts, solange sie
	 * ohnehin zu sehen ist - auf dem breiten Schirm bleibt also alles, wie es
	 * war.
	 */
	private zeigeTafel(): void {
		const feld = this.tafelFeld?.nativeElement;
		if (!feld) return;
		// Erst nach dem Zeichnen: Die Tafel bekommt ihren Inhalt ueber die
		// Aenderungspruefung, vorher ist sie noch leer und steht woanders.
		setTimeout(() => feld.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
	}

	neuZeichnen(): void {
		const feld = this.bildFeld?.nativeElement;
		if (!feld) return;
		feld.replaceChildren();
		if (!this.hatInhalt) return;

		// Dieselbe Zusammenstellung wie fuer die Seite im PDF-Export (grubenbild-daten.ts).
		const stand = grubenbildStand(this.bp, this.aufnahmeTag());
		this.abs = stand.abs;
		this.auflage = stand.auflage;
		// Filz: Tiefen ab Oberkante Ol (filz-tiefen.ts) - im Bild und in der Tafel.
		this.versatz = stand.versatz;

		const svg = zeichneGrubenbild(
			stand.daten,
			{
				gewaehlt: this.gewaehlt,
				substratgrenzen: this.substratgrenzen,
				textur: this.textur,
				grenzlinien: this.grenzlinien,
				wurzeln: this.wurzeln,
				horizonte: this.horizonte,
				substrate: this.substrate,
				beiWahl: (was) => {
					// Noch einmal auf dieselbe Schicht tippen waehlt sie ab
					// (Nutzer-Wunsch 19.09.2026). Ohne das blieb eine einmal
					// gewaehlte Schicht blau umrandet stehen, und es gab keinen
					// Weg zurueck auf „nichts gewaehlt".
					this.gewaehlt = this.gewaehlt === was ? null : was;
					this.neuZeichnen();
					if (this.gewaehlt !== null) this.zeigeTafel();
				},
				beiZeigen: (inhalt, x, y) => {
					this.tipp = inhalt;
					this.tippX = x;
					this.tippY = y;
				},
				beiAuflageSchild: (x, y) => this.lupeUmschalten(x, y),
			},
		);
		feld.appendChild(svg);
	}

	/* --- Die Bodenform ueber der Tafel ------------------------------------- */

	/**
	 * Die Kopfzeile: Feinbodenform mit Wasserstufe, dahinter in Klammern ihre
	 * Langform - zum Beispiel „oBgS (Bergrader Sand-Braunerde)"
	 * (Nutzer-Wunsch 20.09.2026). Vorher stand dort die Hauptbodenform; das
	 * Kuerzel neben dem Kuerzel half niemandem, der die Reihe nicht auswendig
	 * kennt.
	 *
	 * Ist die Langform nicht bekannt - die Feinbodenform steht in keiner Liste,
	 * wie bei allen frei gebildeten Moorformen -, bleibt es bei der
	 * Hauptbodenform. Eine leere Klammer sagt weniger als das Kuerzel.
	 *
	 * Dieselben Texte wie im Boden-Reiter (`bodenform-anzeige.ts`). Wasserform,
	 * Substratfeuchte und Humusform stehen NICHT gesondert dabei
	 * (Nutzer-Entscheidung 19.09.2026): Die Wasserstufe steckt schon in der
	 * Feinbodenform, und der Rest gehoert in den Boden-Reiter.
	 */
	get bodenformOben(): string {
		if (!this.bp) return '';
		const fbf = feinbodenformText(this.bp);
		const klammer = feinbodenformLangform(this.bp, this.langform()) || hauptbodenformText(this.bp);
		if (fbf && klammer) return `${fbf} (${klammer})`;
		return fbf || klammer;
	}

	/**
	 * Der Nachschlag in der Feinbodenformen-Liste, einmal gebaut.
	 *
	 * Der Kopf wird bei jeder Aenderungspruefung neu gelesen; die Liste hat rund
	 * 1300 Zeilen und soll dabei nicht jedes Mal durchgegangen werden. Solange
	 * sie noch leer ist (die Listen kommen aus der Datenbank und sind beim
	 * ersten Blick nicht immer schon da), wird beim naechsten Mal neu gefragt.
	 */
	private langformen?: (fbfCode: string) => string | undefined;
	private langform(): (fbfCode: string) => string | undefined {
		if (!this.langformen) {
			const zeilen = (this.ds.getHardcoded('Feinbodenformen') ?? []) as FeinbodenformZeile[];
			if (!zeilen.length) return () => undefined;
			this.langformen = langformNachschlag(zeilen);
		}
		return this.langformen;
	}

	/** Standortsformengruppe, zum Beispiel „M2(S)_mst_t". */
	get stfgText(): string {
		return this.bp ? stfgString(this.bp) : '';
	}

	/* --- Die Tafel neben dem Bild ------------------------------------------ */

	get gewaehlteAuflage(): AuflageStreifen | null {
		if (typeof this.gewaehlt !== 'string' || !this.gewaehlt.startsWith('auflage:')) return null;
		const feld = this.gewaehlt.slice(8);
		return this.auflage.zeilen.find(z => z.feld === feld) ?? null;
	}

	/** Der gewaehlte Horizont mit seiner Tiefenspanne. */
	get gewaehlterHorizont(): { zeile: HorizontZeile; von: number; bis: number } | null {
		if (typeof this.gewaehlt !== 'number') return null;
		const zeilen = this.bp?.Horizontfolge ?? [];
		const zeile = zeilen[this.gewaehlt];
		if (!zeile) return null;
		const von = this.gewaehlt > 0 ? (zeilen[this.gewaehlt - 1].tiefe ?? 0) : 0;
		const bis = zeile.tiefe ?? 0;
		return bis > von ? { zeile, von, bis } : null;
	}

	/**
	 * Alle Substratschichten, die in den gewaehlten Horizont fallen.
	 *
	 * Ein Horizont umfasst haeufig mehrere Substratzeilen (Grube 033 Torgelow:
	 * der Bb-Horizont von 135 bis 300 cm liegt ueber sieben Substratzeilen). Der
	 * Klick auf den Horizont soll sie alle zeigen - sonst muesste man sie
	 * einzeln zusammensuchen.
	 */
	get substratSchichten(): { von: number; bis: number; zeile: SubstratZeile; lage: WechselLage }[] {
		const h = this.gewaehlterHorizont;
		if (!h) return [];
		const zeilen = this.bp?.Substratfolge ?? [];
		const raus: { von: number; bis: number; zeile: SubstratZeile; lage: WechselLage }[] = [];
		zeilen.forEach((z, i) => {
			const von = i > 0 ? (zeilen[i - 1].tiefe ?? 0) : 0;
			const bis = z.tiefe ?? 0;
			if (bis <= von) return;
			// Ueberschneidet sich die Schicht mit dem Horizont?
			if (bis <= h.von || von >= h.bis) return;
			// Die Lage in der Wechselfolge kommt aus der GANZEN Substratfolge,
			// nicht aus dem Ausschnitt: Eine Kette kann ueber die Grenze des
			// Horizontes hinausreichen.
			raus.push({ von: Math.max(von, h.von), bis: Math.min(bis, h.bis), zeile: z, lage: wechselLage(zeilen, i) });
		});
		return raus;
	}

	get auflageAngaben(): Angabe[] {
		const z = this.gewaehlteAuflage;
		if (!z) return [];
		return [
			{ name: 'Horizont', wert: z.feld },
			{ name: 'Bezeichnung', wert: HORIZONT_FARBEN[z.feld]?.text ?? '' },
			{ name: 'Mächtigkeit', wert: `${z.maechtigkeit} cm` },
			{ name: 'Lage', wert: 'über der Geländeoberfläche' },
		];
	}

	/** Die Horizontsymbole des gewaehlten Horizontes, mit ihrer Farbe. */
	get symbole(): { roh: string; farbe: string; schrift: string; text: string }[] {
		const liste = (this.gewaehlterHorizont?.zeile.horizonte ?? []).filter(Boolean) as string[];
		return liste.map(roh => {
			const farbe = horizontFarbe([roh]).farbe;
			return {
				roh,
				farbe,
				schrift: helligkeit(farbe) < 55 ? '#f4efe6' : '#241f19',
				text: symbolText(roh),
			};
		});
	}

	get praegend(): string | null {
		const liste = this.symbole;
		return liste.length > 1 ? liste[liste.length - 1].roh : null;
	}

	/** Die Symbole des gewaehlten Horizontes als Ueberschrift, z.B. „Ah/Es". */
	get horizontName(): string {
		return symbolfolgeText(this.gewaehlterHorizont?.zeile.horizonte) || 'ohne Horizontsymbol';
	}

	/** Die Angaben EINER Substratschicht, fuer die Liste im Horizont. */
	angabenZu(z: SubstratZeile, lage: WechselLage): Angabe[] {
		const raus: Angabe[] = [];
		const nimm = (name: string, wert: string | number | undefined | null) => {
			if (wert === null || wert === undefined || wert === '') return;
			raus.push({ name, wert: String(wert) });
		};
		nimm('Bodenart', bodenartText(z.substrat));
		nimm('Humusgehalt', this.stufe(z.humusgehalt, HUMUS_STUFEN.map(s => s.bezeichnung)));
		nimm('Skelettgehalt', this.stufe(z.skelettgehalt, SKELETT_STUFEN.map(s => s.bezeichnung)));
		nimm('Kiesgehalt', this.stufe(z.kiesgehalt, SKELETT_STUFEN.map(s => s.bezeichnung)));
		nimm('Steingehalt', this.stufe(z.steingehalt, SKELETT_STUFEN.map(s => s.bezeichnung)));
		nimm('Karbonat', this.stufe(z.karbonatgehalt, KARBONAT_STUFEN.map(s => s.bezeichnung)));
		const ld = LAGERUNGSDICHTE_STUFEN.find(s => s.wert === z.lagerungsdichte);
		nimm('Lagerungsdichte', ld ? `${ld.wert} - ${ld.name}` : undefined);
		nimm('Farbe', z.farbe);
		// Der Haken sagt „wechselt mit der Schicht DARUNTER". Die gemeinte
		// Schicht traegt ihn selbst nicht und gehoert trotzdem dazu - sie
		// bekommt denselben Hinweis (Nutzer-Befund 20.09.2026). Ein Haken in
		// der letzten Zeile verbindet mit nichts; er bleibt stehen, weil er
		// sagt, dass die Angabe unvollstaendig ist.
		nimm('im Wechsel', imWechsel(lage) || z.wechselschichtung ? 'ja' : undefined);
		nimm('Torfarten', (z.torfarten ?? []).filter(Boolean).join(', ') || undefined);
		nimm('Zersetzungsgrad', z.torfZersetzungsgrad);
		nimm('Substanzvolumen', z.substanzvolumen);
		nimm('Schichtlage', z.schichtLage);
		nimm('Perstruktionszone', z.perstruktionszone);
		nimm('Herkunft', z.substratherkunft);
		nimm('Besonderheiten', z.besonderheiten);
		return raus;
	}

	private stufe(wert: number | undefined, namen: string[]): string | undefined {
		if (typeof wert !== 'number' || !Number.isFinite(wert)) return undefined;
		return namen[wert] ? `${wert} - ${namen[wert]}` : String(wert);
	}

	/* --- Legende ------------------------------------------------------------ */

	get legende(): LegendenEintrag[] {
		const gesehen = new Map<string, LegendenEintrag>();
		for (const z of this.bp?.Horizontfolge ?? []) {
			for (const roh of (z.horizonte ?? []).filter(Boolean) as string[]) {
				if (gesehen.has(roh)) continue;
				const g = leseSymbol(roh);
				const kern = istWechsel(g.kern) ? (g.kern.split('↔')[1] ?? '').trim() : g.kern;
				gesehen.set(roh, {
					symbol: roh,
					farbe: horizontFarbe([roh]).farbe,
					text: HORIZONT_FARBEN[kern]?.text ?? '',
				});
			}
		}
		for (const z of this.auflage.zeilen) {
			if (gesehen.has(z.feld)) continue;
			gesehen.set(z.feld, {
				symbol: z.feld,
				farbe: HORIZONT_FARBEN[z.feld].farbe,
				text: HORIZONT_FARBEN[z.feld].text,
			});
		}
		return [...gesehen.values()];
	}

	/** Damit @for eine stabile Kennung hat. */
	spurAngabe(_i: number, a: Angabe): string {
		return a.name;
	}

	spurLegende(_i: number, e: LegendenEintrag): string {
		return e.symbol;
	}
}
